// Aircraft render (M32, AVI-2): a simple box-rig — fuselage + wings + tail — posed
// from the sim aircraft's (pos, yaw, pitch, roll). Client-only (ENG-1); the sim owns
// the flight model. A proper aeroplane model is deferred (like the boat's hull).
import * as THREE from 'three';
import type { Aircraft } from '../../sim/aviation/aircraft';

const BODY = 0xd8d8e0; // light airframe
const TRIM = 0xc03028; // red trim on wings/tail

export class AircraftRenderer {
  private readonly group = new THREE.Group();
  private readonly rigs = new Map<number, THREE.Group>();

  constructor(scene: THREE.Scene) { scene.add(this.group); }

  private buildRig(): THREE.Group {
    const rig = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.6, 2.4), new THREE.MeshLambertMaterial({ color: BODY }));
    const wing = new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.12, 0.7), new THREE.MeshLambertMaterial({ color: TRIM }));
    const tail = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.1, 0.4), new THREE.MeshLambertMaterial({ color: TRIM }));
    const fin = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.6, 0.4), new THREE.MeshLambertMaterial({ color: TRIM }));
    tail.position.z = 1.0; fin.position.set(0, 0.3, 1.0); // tail toward +z (nose is −z)
    rig.add(body, wing, tail, fin);
    return rig;
  }

  update(aircraft: readonly Aircraft[]): void {
    const seen = new Set<number>();
    for (const a of aircraft) {
      seen.add(a.id);
      let rig = this.rigs.get(a.id);
      if (!rig) { rig = this.buildRig(); this.rigs.set(a.id, rig); this.group.add(rig); }
      rig.position.set(a.x, a.y, a.z);
      // three.js YXZ euler: yaw about Y, pitch about X, roll about Z. Our nose is −z
      // and pitch-up is +, matching the sim's bodyAxes frame.
      rig.rotation.set(a.pitch, a.yaw, -a.roll, 'YXZ');
    }
    for (const [id, rig] of this.rigs) {
      if (seen.has(id)) continue;
      this.group.remove(rig);
      rig.traverse((o) => { if (o instanceof THREE.Mesh) o.material.dispose(); });
      this.rigs.delete(id);
    }
  }

  clear(): void {
    for (const rig of this.rigs.values()) {
      this.group.remove(rig);
      rig.traverse((o) => { if (o instanceof THREE.Mesh) o.material.dispose(); });
    }
    this.rigs.clear();
  }
}
